/**
 * @fileoverview Dashboard layout route configuration
 * Provides the main application layout structure with sidebar and header
 */

import type { ReactNode } from "react";
import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";

import { AppErrorBoundary } from "@/components/shared/app-error-boundary";
import { WorkspaceShellStrips } from "@/features/workspace/components/workspace-shell-strips";
import { SandboxEnvironmentBanner } from "@/features/workspace/components/sandbox-environment-banner";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { RouteGuard } from "@/components/auth/route-guard";
import { AdvisorActingAsBanner } from "@/features/dashboard/components/advisor-acting-as-banner";
import { AppHeader } from "@/features/dashboard/components/header";
import { ObligationDrawerProvider } from "@/features/dashboard/components/obligations/obligation-drawer-provider";
import { AppSidebar } from "@/features/dashboard/components/sidebar";
import {
  canUseClientWorkspace,
  isInvoiceFocusedUser,
  isOperatorUser,
} from "@/features/dashboard/lib/access-control";
import { AuthProvider, useAuth } from "@/providers/auth.provider";
import { WorkspaceAppearanceProvider } from "@/providers/workspace-appearance.provider";

function DashboardMainContent() {
  const { claims } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isCommandCenter = pathname === "/dashboard" || pathname === "/dashboard/";
  const showWorkspaceStrips =
    isOperatorUser(claims) && canUseClientWorkspace(claims) && !isCommandCenter;

  return (
    <main
      id="main-content"
      className="flex w-full flex-col gap-4 px-4 pb-8 pt-3 sm:px-6 lg:px-8"
      role="main"
      aria-label="Dashboard content"
    >
      <AdvisorActingAsBanner />
      <SandboxEnvironmentBanner />
      {showWorkspaceStrips ? <WorkspaceShellStrips /> : null}
      <Outlet />
    </main>
  );
}

function DashboardLayoutShell() {
  return (
    <div className="console-shell w-full" role="application" aria-label="Dashboard layout">
      <a
        href="#main-content"
        className="sr-only rounded-md px-4 py-2 text-sm focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:bg-primary focus:text-primary-foreground"
      >
        Skip to main content
      </a>
      <RouteGuard>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <ObligationDrawerProvider>
            <DashboardMainContent />
          </ObligationDrawerProvider>
        </SidebarInset>
      </RouteGuard>
    </div>
  );
}

function DashboardLayout() {
  return (
    <AuthProvider>
      <WorkspaceAppearanceProvider className="contents">
        <DashboardSidebarProvider>
          <DashboardLayoutShell />
        </DashboardSidebarProvider>
      </WorkspaceAppearanceProvider>
    </AuthProvider>
  );
}

function DashboardSidebarProvider({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  const defaultOpen = !isInvoiceFocusedUser(claims);

  return <SidebarProvider defaultOpen={defaultOpen}>{children}</SidebarProvider>;
}

export const Route = createFileRoute("/(app)/dashboard")({
  beforeLoad: ({ location }) => {
    if (location.pathname === "/dashboard/advisor-clients") {
      throw redirect({ to: "/dashboard/advisor/clients" });
    }
  },
  component: DashboardLayout,
  errorComponent: AppErrorBoundary,
});
